import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { KpmgTaxCoreModule } from '@kpmg-tax/core';
import { KpmgTaxUiModule, KpmgTaxUiSvcModule } from '@kpmg-tax/ui';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  AuthGuard,
  CoreModule,
  LOG_SETTINGS_CONFIG,
  TOASTR_SETTINGS_CONFIG,
  VerifiedUserGuard
} from './core';
import { SharedModule } from './shared/shared.module';
import { LogOutComponent } from './logout/logout.component';
import { environment } from 'src/environments/environment';
import {
  InteractionStatus,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  ProtectedResourceScopes
} from '@azure/msal-angular';
import { DndModule } from 'ngx-drag-drop';
import { TooltipModule } from 'ng2-tooltip-directive';
import { HomeComponent } from './home';
import { filter } from 'rxjs/operators';
import { CanDeactivateGuard } from './core/auth/can-deactivate.guard';
import { UserDataService } from './core/services/user-data.service';
import { MaterialModule } from './material/material.module';
import { WelcomeComponent } from './welcome/welcome/welcome.component';
import { SurveyAppComponent } from './survey-app/survey-app.component';
import {
  SurveyListDataPopUpComponent
} from './survey-insight/survey-list/survey-list-data-pop-up/survey-list-data-pop-up.component';
import {
  SurveyListSMEPopUpComponent
} from './survey-insight/survey-list/survey-list-sme-pop-up/survey-list-sme-pop-up.component';
import { ManageStudyComponent } from './manage-study/manage-study.component';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ManageClientUsersComponent } from './manage-study/manage-client-users/manage-client-users.component';
import { LoggedInUserService } from './shared/logged-in-user/logged-in-user.service';
import { UserDetails } from './core/models/user-details.model';
import { getInitials } from './shared/lib/get-intitials';
import { WorkflowExecutionContext, WorkflowRuntime } from './core/workflow/workflow-runtime';
import { WorkflowStep } from './core/workflow/workflow-step';
import { AppInterceptor } from './core/interceptors/app-interceptors';
import { ErrorHandlerService } from './core/services/app-error-handler.service';
import { FavoriteStudiesComponent } from './home/favorite-studies/favorite-studies.component';
import { ClientManagerAuthGaurd } from './core/auth/client-manager-auth.gaurd';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azure.clientId,
      authority: environment.UserProvision.Authority,
      redirectUri: environment.appUrl,
      postLogoutRedirectUri: environment.appUrl,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: 'localStorage'
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [
        'user.read',
        'openid',
        'profile',
        'api://' + environment.azure.clientId + '/user_impersonation'
      ]
    }
  };
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap =
    new Map<string, Array<string | ProtectedResourceScopes>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(environment.appUrl + environment.apiRoot, [
    'api://' + environment.azure.clientId + '/user_impersonation'
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
@NgModule({
  declarations: [
    AppComponent,
    WelcomeComponent,
    HomeComponent,
    SurveyAppComponent,
    LogOutComponent,
    SurveyListDataPopUpComponent,
    SurveyListSMEPopUpComponent,
    ManageStudyComponent,
    ManageClientUsersComponent,
    FavoriteStudiesComponent
  ],
  imports: [
    DndModule,
    BrowserModule,
    FormsModule,
    TooltipModule,
    BrowserAnimationsModule,
    HttpClientModule,
    KpmgTaxCoreModule.withConfig({
      logConfig: LOG_SETTINGS_CONFIG
    }),
    KpmgTaxUiModule,
    KpmgTaxUiSvcModule.withConfig({
      toastrConfig: TOASTR_SETTINGS_CONFIG
    }),
    BsDatepickerModule.forRoot(),
    CoreModule,
    SharedModule,
    NgxDatatableModule,
    ReactiveFormsModule,
    AppRoutingModule,
    MsalModule,
    MaterialModule
  ],
  exports: [MaterialModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: startWorkFlow,
      deps: [MsalService, MsalBroadcastService, UserDataService, LoggedInUserService, Injector],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalGuard,
    MsalBroadcastService,
    MsalService,
    CanDeactivateGuard,
    Title,
    CurrencyPipe, DecimalPipe,
    VerifiedUserGuard,
    AuthGuard, ClientManagerAuthGaurd,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }

export function startWorkFlow(
  msalService: MsalService,
  broadcastService: MsalBroadcastService,
  userDataService: UserDataService,
  loggedInUserService: LoggedInUserService,
  injector: Injector
): () => Promise<any> {
  return (): Promise<any> => {
    const workFlowSteps: WorkflowStep[] = [
      setuserloginStep,
      setActiveAccount
    ];
    const workFlowRun = new WorkflowRuntime(msalService, broadcastService, userDataService, loggedInUserService, injector);
    return workFlowRun.executeWorkFlowStep(workFlowSteps);
  };
}

export function setuserloginStep({ msalService, broadcastService, injector }: WorkflowExecutionContext): () => Promise<any> {
  return (): Promise<any> => {
    if (!msalService || (window.location.href).includes('welcome')) {
      return;
    }
    const scopes = ['api://' + environment.azure.clientId + '/user_impersonation'];
    broadcastService.inProgress$.pipe(filter((status: InteractionStatus) => status === InteractionStatus.None)).subscribe({
      next: (result) => {
        if (result) {
          if (!msalService && !msalService.instance) {
            msalService = injector.get(MsalService);
          }
          let activeAccount = msalService.instance.getActiveAccount();
          if (!activeAccount) {
            if (msalService.instance.getAllAccounts().length > 0) {
              const accounts = msalService.instance.getAllAccounts();
              msalService.instance.setActiveAccount(accounts[0]);
              activeAccount = accounts[0];
            }
            else {
              msalService.loginRedirect({ scopes, extraScopesToConsent: scopes });
              return;
            }
          }
        }
      },
      error: (err) => {
        msalService.loginRedirect({ scopes, extraScopesToConsent: scopes })
      }
    });
  };
}

export function setActiveAccount({ msalService, userDataService, loggedInUserService }: WorkflowExecutionContext): () => Promise<any> {
  return (): Promise<any> => {
    if (!msalService || (window.location.href).includes('welcome')) {
      return;
    }
    msalService.handleRedirectObservable().subscribe(result => {
      if (msalService.instance.getAllAccounts().length > 0) {
        const accounts = msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          msalService.instance.setActiveAccount(accounts[0]);
          userDataService.getUserRolesAndPermissions().subscribe(({ result }) => {
            const activeAccount = msalService.instance.getActiveAccount();
            if (activeAccount !== null && result && result.roleId) {
              const userDetails: UserDetails = {
                userId: result.currentUserId,
                firstName: result.firstName,
                lastName: result.lastName,
                userNameInitials: getInitials(result.firstName, result.lastName),
                email: result.email,
                roleId: result.roleId,
                permissions: result.permissions,
              };
              loggedInUserService.setUserDetails(userDetails);
              if (!result.isFirstLoginDateTimeUpdated) {
                userDataService.updateFirstLoginDateTime();
              }
            }
          });
        }
      }
    });
  };
}
